// @flow
import Promise from 'bluebird';
import _ from 'lodash';
import moment from 'moment';
import * as d3 from 'd3';
import store from '../../../store';
import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_GET_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../../store/modules/overview-panel/types';
import api from '../../api';
import filters from '../../core/filters';
import infoBoard from '../../core/infoboard';
import worldMap from '../../core/map';
import ColorBrewer from '../../core/tools/colorbrewer';
import ColorFlatUI from '../../core/tools/colorflatui';
import translate from '../../core/tools/translate';
import environment from '../../environment';

import Workspace from '../../workspaces';
import { ControlType, Item } from '../../workspaces/item';
import { CoreControl } from '../CoreControl';
import { VectorType } from '../VectorType';
import databoard from '../../core/databoard';
import DataTable from '../../core/charts/Datatable';
import { FeaturePermission } from '../../../constants/feature.permission.helper';
import numeral from 'numeral';
import { notify, NOTIFY_TYPE } from '../../shared/notify';
import { ThemeValuesEnum } from '../../../enuns/AfterSales/themeValues.enum';
import { VehicleTypeEnum } from '../../core/vehicleType/constants';

const LayerInfo = name => require('../../info').get(name);
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

export class ShapesAfterSalesControl extends CoreControl {
    _tags;
    _cachePages;
    _date;

    constructor(options) {
        super(options);
        this._manualPaints = new Map();

        (this)._calculateScale = this._calculateScale.bind(this);
        (this)._change = this._change.bind(this);
        (this)._changePeriod = this._changePeriod.bind(this);
        (this)._changeTheme = this._changeTheme.bind(this);
        (this)._colorizeStaticShape = this._colorizeStaticShape.bind(this);
        (this)._disable = this._disable.bind(this);
        (this)._filterCallback = this._filterCallback.bind(this);
        (this)._getCheckboxOptions = this._getCheckboxOptions.bind(this);
        (this)._getLayerOptions = this._getLayerOptions.bind(this);
        (this)._toggleFilterByAreas = this._toggleFilterByAreas.bind(this);
        (this)._toggleFilterByLocation = this._toggleFilterByLocation.bind(this);
        (this)._toggleFilterByPoints = this._toggleFilterByPoints.bind(this);
        (this)._toggleLabel = this._toggleLabel.bind(this);
        (this).colorize = this.colorize.bind(this);
        (this).createFilter = this.createFilter.bind(this);
        (this).getTypeValue = this.getTypeValue.bind(this);
        (this).reload = this.reload.bind(this);
        (this).select = this.select.bind(this);
        (this).setFillColor = this.setFillColor.bind(this);
        (this).setLineColor = this.setLineColor.bind(this);
        (this).setLineWeight = this.setLineWeight.bind(this);
        (this).setRange = this.setRange.bind(this);
        (this).toggleOverview = this.toggleOverview.bind(this);
        (this).getDate = this.getDate.bind(this);
    }

    hasDynamicThemes() {
        return _.size(this.options.themes) > 0;
    }

    init() {
        this._tags = {
            tagName: {
                property: 'Nome',
                box: true,
                size: 9,
                offsetY: 14,
                ignore: value => Number(value) === 0
            },
            tagValue: {
                property: 'TotalFormatted',
                offsetY: -5,
                size: 10,
                colorize: true,
                bold: true,
                box: true,
                ignore: value => Number(value) === 0
            },
            tagPgts: {
                property: 'Pgts',
                offsetY: -24,
                size: 10,
                bold: true,
                box: true,
                ignore: value => Number(value) === 0
            },
        };

        const values = {
            help: undefined,
            id: `layer-${this.options.id}`,
            input: 'checkbox',
            label: translate(`Common.${this.options.name}`),
            name: this.options.name,
            layer: this.getLayer,
            type: this.options.type
                ? this.options.type
                : this.hasDynamicThemes()
                    ? 'vector-layers-extended'
                    : 'vector-layers',
            overview: this.options.overview ? this.toggleOverview : null,
            change: this._change,
            tab: () => {
                return $(`#${this.options.id}-tab-link`);
            },
            scheme: this.getTypeValue,
            fillColor: {
                name: 'fillColor',
                change: this.setFillColor
            },
            lineColor: {
                name: 'lineColor',
                change: this.setLineColor
            },
            lineWeight: {
                change: this.setLineWeight
            },
            range: {
                change: this.setRange
            },
            filter: this.options.filter && this.hasDynamicThemes() ? this.createFilter : false,
            links: {},
            checkbox: this._getCheckboxOptions(),
            dropdown: [],
            multiselect: [],
        };

        if (_.size(this.options.types)) {
            const dataSource = _.map(this.options.types, t => ({ text: t.name, value: t.id }));
            values.dropdown.push({
                name: 'list',
                dataSource,
                change: this.select,
                visible: _.size(this.options.types) >= 1
            });
        }

        if (_.size(this.options.themes)) {
            const dataSource = _.reduce(
                this.options.themes,
                (acc, theme) => {
                    const themes = _.size(this.options.types)
                        ? this.options.types[_.keys(this.options.types)[0]].themes
                        : _.map(this.options.themes, e => e.id);

                    if (_.indexOf(themes, theme.id) > -1 || _.indexOf(_.map(themes, x => x.id), theme.id) > -1) {
                        acc.push({ text: translate(theme.name), value: theme.id });
                    }

                    return acc;
                },
                _.size(this.options.types) ? [{ text: translate('Common.Theme'), value: 0 }] : []
            );

            values.dropdown.push({
                extends: true,
                name: 'data',
                dataSource,
                help: translate('Help.ThemeInfo'),
                change: this._changeTheme,
                disable: this._disable,
                separator: _.size(this.options.types) > 0
            });

            values.dropdown.push({
                extends: true,
                name: 'periods',
                dataSource: [({ text: 'Período', value: '0' })],
                change: (period) => {
                    this._getPeriodByThemes()
                        .filter(p => parseInt(p.value) === parseInt(period))
                        .map(p => this._setPeriod(p.text));

                    this._updatePopover();
                    this.reload();
                },
                separator: true,
                visible: false,
                disable: this._disable
            });
        }

        if (!this._instance) {
            this._instance = new VectorType(values);
        }

        this._cachePages = new Map();
    }

    _logAction(actionName) {
        api.general.log.action(
            environment._getContextEnum(this.options.name),
            actionName,
            JSON.stringify({ TipoLocalidade: this.getLocaleName(), Tema: this.getThemeText() }));
    }

    show(options = {}, features = this.getLayer().getCurrentFeatures()) {
        if ($(`#${this.options.id}-collapse`).length === 0 || options.refresh) {
            if (options.empty) {
                this._view('create', null);
            } else {
                this._view('create', features);
            }
        }

        if (options.show) {
            infoBoard.open();
            if (options.id) {
                $(`#${this.options.id}-title-${options.id}`).trigger('click');
            }

            setTimeout(() => {
                $(`#${this.options.id}-tab-link`).trigger('click');
            }, 100);
        }
    }

    select(value, options) {
        if (_.isString(value)) {
            value = Number(value);
        }

        if (options && !_.isString(options)) {
            this.getLayer().setOptions(options);
        }

        if (this.getLayer().visible()) {
            this._logAction('Update Localidade');
        }
        this._resetTheme(value);
        this._uncheckAll();
        this._unhighlightArea(false);

        try {
            const { remote, scaleRange } = this.getLayer().options;

            scaleRange.setOptions({ id: value });
            remote.params.locationType = value;
        } catch (err) {
            console.log(err);
        }

        if (this.check() && this.getLayer().visible()) {
            this.getLayer().reset();
        }
    }

    _resetTheme(value) {
        try {
            if (typeof value === 'undefined' || value === null || value < 0) {
                value = this.getControls().dropdown.list.value;
            }

            if (this.getControls().dropdown.data) {
                const dataSource = new kendo.data.DataSource({
                    data: _.reduce(this.options.themes, (acc, theme) => {
                        const t = (_.find(this.options.types, e => e.id === value));
                        if (t && t.themes && _.indexOf(t.themes, theme.id) > -1) {
                            acc.push({ text: theme.name, value: theme.id });
                        }

                        return acc;
                    }, [{ text: 'Tema', value: 0 }])
                });

                dataSource.read();
                const currentIndex = _.findIndex(
                    dataSource.view(),
                    d => d.value === Number(this.getControls().dropdown.data.value)
                );

                this.getControls().dropdown.data.input.setDataSource(dataSource);
                this.getControls().dropdown.data.input.select(currentIndex > 0 ? currentIndex : 0);
                this.getControls().dropdown.data.index = currentIndex > 0 ? currentIndex : 0;
                this.getControls().dropdown.data.text = currentIndex > 0 ? this.getControls().dropdown.data.text : 'None';
                this.getControls().dropdown.data.value = currentIndex > 0 ? this.getControls().dropdown.data.value : 0;

                this._updatePopover();
            }
        } catch (err) {
            console.log(err);
        }
    }

    colorize(featureCollection, layer = this.getLayer()) {
        return new Promise((resolve) => {
            const id = layer.getMap()._id;
            const themeId = this.getThemeValue(id);

            if (!themeId) {
                resolve(featureCollection);
            } else {
                this.options.request(this._getLayerOptions(featureCollection, themeId, id))
                    .then(res => {
                        if (!_.size(res) || (!_.some(res, r => r.value))) {
                            resolve(this._setFeaturesValue(featureCollection, {}));
                        }

                        resolve(this._colorizeStaticShape(id, featureCollection, res));
                    });
            }
        });
    }

    setRange() {
        if (this.getThemeValue() && this.check() && this.getLayer().visible()) {
            const layer = this.getLayer();
            const id = layer.getMap()._id;
            const featureCollection = this._colorizeStaticShape(id, layer.toGeoJSON(), this._cachePages.get(id));
            layer.setFeatures(featureCollection, true);
            this._applyManualColors(layer);
        }
    }

    setFillColor(value) {
        const layer = this.getLayer();

        const color = value.split(':')[0];
        const index = value.split(':')[1];

        const { symbology } = layer.options;
        symbology.theme = ColorBrewer[color][index];

        layer.setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            layer.reload();
        }
    }

    setLineColor(value) {
        const layer = this.getLayer();

        const color = value.split(':')[0];
        const index = value.split(':')[1];

        const { symbology } = layer.options;
        symbology.style.color = ColorFlatUI[color][index][0];

        layer.setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            layer.reload();
        }
    }

    setLineWeight(value) {
        const layer = this.getLayer();

        const { symbology } = layer.options;
        symbology.style.weight = value;

        layer.setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            layer.reload();
        }
    }

    getDate(id = this.getLayer().getMap()._id) {
        if (_.isUndefined(this._date)) {
            this._date = new Map();
        }

        if (!this._date.has(id)) {
            if (id === 0)
                this._date.set(id, { start: moment(environment.get('CONFIG.FILTERS.ENDYEAR')).startOf('year'), end: moment(environment.get('CONFIG.FILTERS.ENDYEAR')) });
            else
                this._date.set(id, this._date.get((id - 1)));
        }

        return this._date.get(id);
    }

    getThemeValue(id = this.getLayer().getMap()._id) {
        return (() => {
            try {
                return Number(this.getControls(id).dropdown.data.value);
            } catch (err) {
                console.log(err);
                return '';
            }
        })();
    }

    getThemeText(id = this.getLayer().getMap()._id) {
        try {
            return this.getControls(id).dropdown.data.text;
        } catch (error) {
            return '';
        }
    }

    getLocaleName(id = this.getLayer().getMap()._id) {
        try {
            return this.getControls(id).dropdown.list.text;
        } catch (error) {
            return '';
        }
    }

    getIdMap() {
        return this.getLayer().getMap()._id;
    }

    getTypeValue(realValue) {
        const _value = (() => {
            try {
                return Number(this.getControls().dropdown.list.value);
            } catch (err) {
                return environment.get('TYPE.AUTO');
            }
        })();

        if (realValue) {
            return _value;
        } else if (_value === environment.get('TYPE.AUTO')) {
            return (this.getZoomLocation('ibge') || {}).id;
        }

        return _value;
    }

    getZoomLocation(type = 'ibge') {
        const areas = {
            ibge: environment.get('CONFIG.FILTERS.AREAS_TYPES'),
            client: environment.get('CONFIG.FILTERS.MARKED_AREA_TYPES')
        };

        try {
            return _(areas[type])
                .filter(z => z.zoom)
                .find(z => _.inRange(worldMap.getZoom(), z.zoom.min - 1, z.zoom.max + 1));
        } catch (err) {
            console.log(err);
            return {};
        }
    }

    getTypeText() {
        try {
            return this.getControls().dropdown.list.text;
        } catch (err) {
            console.log(err);
            return 'None';
        }
    }

    reload(skipManualColors = false) {
        if (this.check() && this.getLayer().visible()) {
            const layer = this.getLayer();
            this._cachePages = new Map();
            this.colorize(layer.toGeoJSON(), layer)
                .then(res => {
                    if (skipManualColors) {
                        res.features.forEach(f => {
                            delete f.properties._fillColor;
                        });
                    }

                    layer.setFeatures(res, true);

                    if (!skipManualColors) {
                        this._applyManualColors(layer);
                    }
                });
        }
    }

    createFilter() {
        filters.create({
            container: document.getElementById('filter-container'),
            id: `filter-${_.kebabCase(this.options.name)}-${this.getThemeValue()}`,
            label: translate(`Common.${this.options.name}`),
            type: 'afterSalesShape',
            showFilterPGTS: false,
            direction: 'up',
            onSubmit: this._filterCallback,
            fields: {
                segments: true,
                vehicleTypes: true,
                brands: true,
                families: true,
                fuelTypes: true
            }
        });
    }

    updateFilter() {
        const layer = this.getLayer();

        const tags = Array.isArray(layer.options.tag)
            ? layer.options.tag
            : (layer.options.tag ? [layer.options.tag] : []);

        const showFilterPGTS = tags.some(tag => tag.property === 'Pgts');

        filters.updateControls({
            container: document.getElementById('filter-container'),
            id: `filter-${_.kebabCase(this.options.name)}-${this.getThemeValue()}`,
            label: translate(`Common.${this.options.name}`),
            type: 'afterSalesShape',
            showFilterPGTS: showFilterPGTS,
            direction: 'up',
            onSubmit: this._filterCallback,
            fields: {
                segments: true,
                vehicleTypes: true,
                brands: true,
                families: true,
                fuelTypes: true
            }
        });
    }

    toggleOverview() {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);

        const features = layer.getCurrentFeatures();

        if (!store.getters[OVERVIEW_PANEL_IS_VISIBLE] || !_.isEqual(this._overviewFeatures, features)) {
            api.general.log.action(environment._getContextEnum(this.options.name), 'Visualizar Gráfico');
        }

        if (
            (_.size(features) && !_.isEqual(this._overviewFeatures, features)) ||
            !store.getters[OVERVIEW_PANEL_IS_VISIBLE]
        ) {
            this.summary(features);
            this._overviewFeatures = _.cloneDeep(features);

            store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
        }
    }

    _updatePopover() {
        try {
            this.getControls().button.popover.target[0].dataset.content = translate(
                `Help.${(
                    _(this.options.themes)
                        .map((val, key) => ({
                            id: val.id,
                            key: val.key
                        }))
                        .find(val => val.id === this.getThemeValue()) || { key: 'ThemeInfo' }
                ).key
                }`);
        } catch (err) {
            console.log('ERROR: ', err);
        }
    }

    _uncheckAll() {
        _.forEach(this.getControls().checkbox, control => {
            control.input.iCheck('uncheck');
        });
    }

    _change(e) {
        this._logAction(e.checked ? 'enable' : 'disable');
        const layer = this.getLayer();

        if (e.checked) {
            try {
                layer.options.scaleRange.setOptions({ id: this.getTypeValue(true) });
            } catch (err) {
                console.log('WARNING: no autozoom level');
            }

            this._setFilterButtonVisibility(true);
            layer.redraw();
        } else {
            layer.hide();
            infoBoard.toggleDashboard(false);
            this._view('reset');
            this.show({ empty: true });
            this._setFilterButtonVisibility(false);
            this._uncheckAll();
            this._manualPaints.clear();
            this.reload(true);
        }

        Workspace.addAction(new Item(`#${$(e).prop('id')}`, e.checked, ControlType.ICheckBox));
    }

    _view(method, feature) {
        try {
            const instance = LayerInfo(this.options.name);

            if (instance) {
                if (method === 'reset') {
                    return instance[method]();
                } else {
                    return instance[method](feature);
                }
            }
        } catch (err) {
            console.log(err);
        }
    }

    _getLayerOptions(
        featureCollection = this.getLayer().toGeoJSON(),
        themeId = (this).getThemeValue(),
        id
    ) {
        const filterData = filters.get(`filter-${_.kebabCase(this.options.name)}-${this.getThemeValue()}`, id) || {};

        let typeId;
        try {
            typeId = Number(this.getControls(id).dropdown.list.value);
        } catch (err) {
            console.log('ERROR: No location type specified');
        }
        const options = {
            mapThemeId: themeId,
            listLocaleIds: featureCollection.features.map(f => f.properties.Id),
        };

        if (this.options.period.initial && this.options.period.final) {
            options.yearInitial = this.options.period.initial;
            options.yearEnd = this.options.period.final;
        }

        if (filterData && filterData.segments && filterData.segments.value) {
            options.segmentIds = filterData.segments.value;
        }

        if (filterData && filterData.brands && filterData.brands.value) {
            options.vehicleBrandIds = _.isArray(filterData.brands.value)
                ? filterData.brands.value
                : [filterData.brands.value];
        }

        if (filterData && filterData.vehicleTypes && filterData.vehicleTypes.value) {
            options.vehicleTypeIds = _.isArray(filterData.vehicleTypes.value)
                ? filterData.vehicleTypes.value
                : [filterData.vehicleTypes.value];
        }

        if (filterData && filterData.families && filterData.families.value) {
            options.familiesIds = _.isArray(filterData.families.value)
                ? filterData.families.value
                : [filterData.families.value];
        }

        if (filterData && filterData.fuelTypes && filterData.fuelTypes.value) {
            options.fuelTypeIds = _.isArray(filterData.fuelTypes.value)
                ? filterData.fuelTypes.value
                : [filterData.fuelTypes.value];
        }

        if (filterData && filterData.pgtsType && filterData.pgtsType.value) {
            options.trafficGeneratorPoleTypes = _.isArray(filterData.pgtsType.value)
                ? filterData.pgtsType.value
                : [filterData.pgtsType.value];
        }

        if (filterData && filterData.companySize && filterData.companySize.value) {
            options.trafficGeneratorPoleCompanySizes = _.isArray(filterData.companySize.value)
                ? filterData.companySize.value
                : [filterData.companySize.value];
        }

        if (filterData && filterData.engineType && filterData.engineType.value) {
            options.engineType = _.isArray(filterData.engineType.value)
                ? filterData.engineType.value
                : [filterData.engineType.value];
        }

        return options;
    }

    _disable() {
        this.getControls().colorpicker.fillColor.input.enable(false);
        this.getControls().colorpicker.fillColor.enable = false;
        try {
            this.getControls().button.filter.input.enable(false);
            this.getControls().button.filter.enable = false;
        } catch (err) {
            console.log('No Filter Button');
        }
        this._updatePopover();
        this._unhighlightArea();
    }

    _changePeriod(date, id = this.getLayer().getMap()._id) {
        this._date.set(id, {
            start: moment(date.start),
            end: moment(date.end)
        });

        api.general.log.action(environment._getContextEnum(this.options.name), 'Mudou Período', JSON.stringify(date));
        this.reload();
    }

    // trigger event when filtered data
    _filterCallback() {
        if (this.check() && this.getLayer().visible()) {
            api.general.log.action(environment._getContextEnum(this.options.name), 'Mudança Filtro');
            this.reload();
        }
    }

    _toggleFilterByPoints(val) {
        api.general.log.action(environment._getContextEnum(this.options.name), 'Pontos nas áreas selecionadas');
        return LayerInfo(this.options.name).setFilterByPoints(Boolean(val));
    }

    _toggleFilterByLocation(val) {
        api.general.log.action(environment._getContextEnum(this.options.name), 'Filtrar Área(s) selecionada(s)');
        return LayerInfo(this.options.name).setFilterByLocation(Boolean(val));
    }

    _toggleFilterByAreas(val) {
        api.general.log.action(environment._getContextEnum(this.options.name), 'Interseção de áreas selecionadas');
        return LayerInfo(this.options.name).setFilterByAreas(Boolean(val));
    }

    _setFilterButtonVisibility(state) {
        if (this.options.filter) {
            try {
                this.getControls().button.filter.input.enable(state);
                this.getControls().button.filter.enable = state;

                if (state) {
                    this.getControls().button.filter.type = this.getThemeValue();
                    this.createFilter();
                }

                if (!state) {
                    filters.close();
                }
            } catch (err) {
                console.log('ERROR: ', err);
            }
        }

        try {
            this.getControls().button.overview.input.enable(state);
            this.getControls().button.overview.enable = state;
            if (store.getters[OVERVIEW_PANEL_GET_CURRENT] === this.options.name && !state) {
                databoard.hide();
                delete this._overviewFeatures;
            }
        } catch (err) {
            console.log(err);
        }
    }

    _getTagPropertyValue(property) {
        switch (property) {
            case 'value':
                const mask = {
                    mask: (_.find(this.options.themes, t => t.id === this.getThemeValue()) || {}).mask
                };
                return _.assign({}, this._tags.tagValue, mask);
            case 'isDrugstore':
                return _.assign({}, this._tags.tagDrugstore);
            case 'Pgts':
                return _.assign({}, this._tags.tagPgts);
            default:
                return _.assign({}, this._tags.tagName);
        }
    }

    _getLayerTag() {
        if (!this.getControls().checkbox) {
            return undefined;
        }

        const tags = [];

        if ((this.getControls().checkbox.tagName || {}).status) {
            tags.push(this._getTagPropertyValue('name'));
        }

        if ((this.getControls().checkbox.tagValue || {}).status) {
            tags.push(this._getTagPropertyValue('value'));
        }

        if ((this.getControls().checkbox.tagPgts || {}).status) {
            tags.push(this._getTagPropertyValue('Pgts'));
        }

        if ((this.getControls().checkbox.tagDrugstore || {}).status) {
            tags.push(this._getTagPropertyValue('isDrugstore'));
        }

        return tags.length === 1 ? tags[0] : tags;
    }

    _setLayerLabelOptions(checked, name) {
        const layer = this.getLayer();
        if (!checked || !this._tags || !this._tags[name]) {
            layer.setOptions({ tag: this._getLayerTag() });

            if (layer.visible()) {
                layer.reload();
            }
            return;
        }

        const currentTagOption = layer.options.tag;
        const currentTags = Array.isArray(currentTagOption)
            ? [...currentTagOption]
            : currentTagOption
                ? [currentTagOption]
                : [];

        const newTag = this._tags[name];

        const updatedTags = currentTags.filter(tag => tag.property !== newTag.property);

        if (checked) {
            updatedTags.push(newTag);
        }

        layer.setOptions({
            tag: updatedTags.length === 1 ? updatedTags[0] : updatedTags.length > 1 ? updatedTags : undefined
        });

        if (layer.visible()) {
            layer.reload();
        }
    }

    _toggleLabel(isChecked, name) {
        api.general.log.action(environment._getContextEnum(this.options.name), `${isChecked ? 'Show' : 'Hide'} ${name}`);
        this._setLayerLabelOptions(isChecked, name);
    }

    _calculateScale(data) {
        let source = data.consolidate || data;
        const values = _.map(source, (v, k) => v);
        const scale = this.getControls().slider.range.value;
        const max = _.max(values);
        const min = _.min(values);
        return {
            min: (Math.abs(min) / 10) * 0.25 * Math.pow(Math.E, 0.3689 * scale) * -1,
            max: (max / 10) * 0.35 * Math.pow(Math.E, 0.3689 * scale)
        };
    }

    _setFeaturesValue(featureCollection, values) {
        const result = {
            type: 'FeatureCollection',
            features: _.map(featureCollection.features, f => {
                const total = values.consolidate && values.consolidate[f.properties.Id] ? values.consolidate[f.properties.Id] : 0;
                const pgts = values.consolidatePGT && values.consolidatePGT[f.properties.Id] ? values.consolidatePGT[f.properties.Id] : 0;
                f.properties.Pgts = numeral(pgts).format('0,0');
                f.properties.Total = total;
                f.properties.TotalFormatted = numeral(total).format('0,0');
                return f;
            })
        };

        return result;
    }

    _unhighlightArea(reload = true) {
        const layer = this.getLayer();

        this._cachePages = new Map();

        layer.setOptions({
            legend: false
        });

        layer.setFeatures(
            this._setFeaturesValue(layer.toGeoJSON(), {}),
            this.check() && this.getLayer().visible() && reload
        );
    }

    _changeTheme() {
        if (this.getLayer().visible()) {
            this._logAction('Update Tema');
        }
        this._manualPaints.clear();

        this._visibleDropdownPeriod();

        this.getControls().colorpicker.fillColor.input.enable(true);
        this.getControls().colorpicker.fillColor.enable = true;
        this.getControls().slider.range.input.enable(true);
        this.getControls().slider.range.enable = true;
        this.getControls().slider.range.input.value(10);
        this.getControls().slider.range.value = 10;

        this.getControls().slider.opacity.input.value(5);
        this.getControls().slider.opacity.value = 5;

        this._loadPeriodByTheme();
        this._togglePGTSVisibility();

        this.getControls().button.overview.input.enable(true);
        this.getControls().button.overview.enable = true;
        this._setFilterButtonVisibility(true);
        this.updateFilter();
        this._updatePopover();
        this.reload(true);
    }

    _togglePGTSVisibility() {
        const isMotorcycle = environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto;
        const $pgtsRow = $(`#${this.options.name.toLowerCase()}-checkbox .extra-text[title="PGTS"]`)
            .closest('.actions-checkbox');

        if (this._checkThemes(['vehicleFleetArea', 'repairableFleetArea']) &&
            environment.get('CONFIG.FILTERS.FILTERBY') &&
            !isMotorcycle) {
            $pgtsRow.show();
        } else {
            $pgtsRow.hide();
        }
    }

    _visibleDropdownPeriod() {
        if (this._checkThemes(['vehicleFleetArea', 'repairableFleetArea']) && environment.get('CONFIG.FILTERS.FILTERBY')) {
            this.getControls().dropdown.periods.input.enable(true);
            this.getControls().dropdown.periods.enable = true;
            this.getControls().dropdown.periods.input.wrapper.show();
        } else {
            this.getControls().dropdown.periods.input.enable(false);
            this.getControls().dropdown.periods.enable = false;
            this.getControls().dropdown.periods.input.wrapper.hide();
        }
    }

    _getPeriodByThemes() {
        const themeKey = this._getThemeKeySelected();
        const periods = [
            { text: 'Período', value: 0 },
            ...environment.get("CONFIG.FILTERS.FILTERBY")
                .filter(x => x.type === `Areas-${themeKey}`)
                .flatMap(x => x.options)
                .map(x => ({ text: x.nome, value: x.id }))
        ];

        return periods;
    }

    _loadPeriodByTheme() {
        const periods = this._getPeriodByThemes();
        const lastValue = periods.length - 1;
        this.getControls().dropdown.periods.input.setDataSource(periods);
        this.getControls().dropdown.periods.input.select(periods[lastValue].value);
        this._setPeriod(periods[lastValue].text);
    }

    _setPeriod(periods) {
        this.options.period = {
            initial: periods.split('-')[0],
            final: periods.split('-')[1]
        };
    }

    _getThemeKeySelected() {
        const themes = Object.keys(this.options.themes).map(t => ({ id: this.options.themes[t].id, key: this.options.themes[t].key }));
        const themeValue = this.getControls().dropdown.data.input.value();
        const themeKey = themes.filter(x => x.id === parseInt(themeValue))[0].key;
        return themeKey;
    }

    _colorizeStaticShape(id, featureCollection = this.getLayer().toGeoJSON(), values) {
        const filterData = filters.get(`filter-${_.kebabCase(this.options.name)}-${this.getThemeValue()}`, id);

        if (!_.size(values)) {
            this._cachePages.set(id, undefined);
            return featureCollection;
        }

        this._cachePages.set(id, values);
        const { symbology } = this.getLayer().options;
        const scale = this._calculateScale(this._cachePages.get(id));
        const legend = {};
        let nameOfLegend = `${this.getThemeText(id)} - ${this.getFormattedName(filterData)}`;
        legend.title = nameOfLegend;
        const value = this.getThemeValue();

        if (value !== ThemeValuesEnum.PGTS && value !== ThemeValuesEnum.MotorcyclePartsAndWorkshop && value !== ThemeValuesEnum.SocialClass) {
            legend.subtitle = `${this.options.period.initial} até ${this.options.period.final}`;
        }

        if (value === ThemeValuesEnum.MotorcyclePartsAndWorkshop) {
            legend.title = this.getFormattedName(filterData);
        }

        legend.mask = (_.find(this.options.themes, t => t.id === +this.getThemeValue(id)) || {}).mask;

        symbology.max = scale.max;
        symbology.min = scale.min;

        symbology.style.fillOpacity = 0.5;

        if (this._checkThemes(['socialClass'])) {
            this._configureSocialClassSymbology(symbology);
            legend.type = 'uniq';
            legend.title = this.getThemeText(id);
        }

        this.getLayer(id).setOptions({
            legend,
            symbology,
            tag: this._getLayerTag()
        });

        return this._setFeaturesValue(featureCollection, this._cachePages.get(id));
    }

    _configureSocialClassSymbology(symbology) {
        const socialClassThemeColors = ['rgb(217,95,95)', 'rgb(224,135,123)', 'rgb(235,175,155)', 'rgb(245,211,191)', 'rgb(255,211,229)', 'rgb(255,241,227)'];
        symbology.theme = socialClassThemeColors;
        symbology.style.fillOpacity = 0.8;
        symbology.style.weight = 0.5;

        return symbology;
    }

    _checkThemes(themes, id, value) {
        const validThemes = themes.filter(p => this.options.themes[p]).map(p => this.options.themes[p].id);
        return _.some(validThemes, v => v === (value || this.getThemeValue(id)));
    }

    _getCheckboxOptions() {
        const res = {};
        if (_(this.options.types).filter((val, key) => key !== 'DINAMICO').size()) {
            res[translate('Common.LocationName')] = {
                name: 'tagName',
                displaySection: true,
                firstOfSection: true,
                change: (isChecked) => this._toggleLabel(isChecked, 'tagName'),
                style: {
                    change: (tag) => {
                        this._tags.tagName = tag;
                        this._toggleLabel();
                    },
                    options: this._tags.tagName
                }
            };
        }

        if (this.hasDynamicThemes()) {
            res[translate('Common.ThemeValue')] = {
                name: 'tagValue',
                displaySection: true,
                change: (isChecked) => this._toggleLabel(isChecked, 'tagValue'),
                style: {
                    change: (tag) => {
                        this._tags.tagValue = tag;
                        this._toggleLabel();
                    },
                    options: this._tags.tagValue
                }
            };

            res[translate('Common.PGTS')] = {
                name: 'tagPgts',
                visible: false,
                displaySection: true,
                change: (isChecked) => {
                    this._toggleLabel(isChecked, 'tagPgts');
                    this.updateFilter();
                },
                style: {
                    change: (tag) => {
                        this._tags.tagPgts = tag;
                        this._toggleLabel();
                    },
                    options: this._tags.tagPgts
                }
            };

            if (isAuthorized(FeaturePermission.LAYER_SELECTED_AREA.manualColorful)) {
                res[translate('Common.AreasSelected')] = {
                    name: 'tagChangeColor',
                    displaySection: true,
                    change: {},
                    style: {
                        change: ({ color }) => {
                            this._paintSelection(color);
                        },
                        isColor: true,
                    }
                };

            }

        }

        if (_(this.options.types).filter((val, key) => key !== 'DINAMICO').size()) {
            res[translate('Common.SelectedLocation')] = {
                name: 'filterLocation',
                filterSection: true,
                firstOfSection: true,
                change: this._toggleFilterByLocation,
            };
            res[translate('Common.PointsInsideLocations')] = {
                name: 'filterPoints',
                filterSection: true,
                change: this._toggleFilterByPoints,
            };
            res[translate('Common.AreasInsideLocations')] = {
                name: 'filterAreas',
                filterSection: true,
                change: this._toggleFilterByAreas,
            };
        }

        return res;
    }

    getFormattedName(data) {
        const pgtsTypeList = environment.get('CONFIG.FILTERS.PGTS');

        if (this.getThemeValue() === ThemeValuesEnum.MotorcyclePartsAndWorkshop && typeof data.pgtsType === 'undefined') {
            return translate('Common.PgtsMotoAndWorkshopAreaData');
        }

        if (!Array.isArray(pgtsTypeList)
            || !data.pgtsType
            || !Array.isArray(data.pgtsType.text)
            || (this.getThemeValue() !== ThemeValuesEnum.PGTS && this.getThemeValue() !== ThemeValuesEnum.MotorcyclePartsAndWorkshop)) {
            return `${translate('Common.All')}`;
        }

        const allPgtsTexts = pgtsTypeList.map(item => item.nome);
        const selectedTexts = data.pgtsType.text;

        const hasAll = allPgtsTexts.every(type => selectedTexts.includes(type));

        if (hasAll) {
            return `${translate('Common.All')}`;
        }

        if (selectedTexts.length === 1) {
            return selectedTexts[0];
        }

        return selectedTexts.slice(0, -1).join(", ") + " e " + selectedTexts.slice(-1)[0];
    }

    summary(features) {
        const data = this.calculate(features);
        const dataStructure = {
            row: translate('Common.Name'),
            column: [translate('Common.Total')]
        };

        const chartPie = new DataTable(data.perPercentage, dataStructure);
        const chartColumn = new DataTable(data.perTotal, dataStructure);

        const date = this.getDate();
        let options = {
            title: {
                table: `${translate(`Common.${this.options.name}`)} `,
                chartPie: `RANKING ${this.getThemeText()} (%)`,
                chartColumn: `RANKING ${this.getThemeText()}`,
            },
            dash: true,
            titleHeader: true,
            titleHeaderMessage: `Sumário - ${this.getLocaleName()} | ${date.start.format('MM/YYYY')} - ${date.end.format('MM/YYYY')}`,
            data: [data.summary, chartPie, chartColumn],
            format: [
                '{0:n0}',
                '{0:p1}',
                '{0:n0}'
            ],
            convertTable: false,
            columns: [
                {
                    field: 'name',
                    title: translate('Common.Name')
                },
                {
                    field: 'total',
                    title: translate('Common.Total'),
                    width: 100,
                    template: data =>
                        numeral(data.total).format('0,0')
                },
                ...(!this._checkThemes(['marketShare'])
                    ? [
                        {
                            field: 'percentage',
                            title: `${translate('Common.Total')} (%)`,
                            width: 100,
                            template: data => numeral(data.percentage).format('0,0[.]00%')
                        }
                    ]
                    : [])
            ],
            buttons: {
                // custom: exportCustomButton
                fullscreen: true,
                custom: isAuthorized(FeaturePermission.LAYER_SELECTED_AREA.exportDataPanel) ? this.getButtonExport(this.options) : undefined
            }
        };

        databoard.show(options);
    }

    calculate(features) {
        const total = _.sumBy(features, f => f.properties.Total);

        const perTotal = _(features)
            .filter(f => f.properties.Total)
            .orderBy(f => f.properties.Total)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.properties.Nome,
                [translate('Common.Total')]: f.properties.Total
            }))
            .value();

        let perPercentage = _(features)
            .filter(f => f.properties.Total)
            .orderBy(f => f.properties.Total)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.properties.Nome,
                [translate('Common.Total')]: f.properties.Total / total
            }))
            .value();

        const summary = _(features)
            .orderBy(f => f.properties.Total)
            .reverse()
            .map(f => ({
                name: f.properties.Nome,
                total: f.properties.Total,
                percentage: f.properties.Total / total
            }))
            .value();

        return {
            perTotal,
            perPercentage,
            summary
        };
    }

    _paintSelection(color) {
        const selectedLayer = this.getLayer();

        const selectedAreaLayer = selectedLayer.toGeoJSON().features.filter(f =>
            require('../../info').get('SelectedAreasAfterSales').check(f.properties.Id)
        );

        const markedAreasLayer = selectedLayer.toGeoJSON().features.filter(f =>
            require('../../info').get('MarkedAreaAfterSales').check(f.properties.Id)
        );

        let selectedFeatures = selectedAreaLayer;

        if (!selectedFeatures.length) {
            selectedFeatures = markedAreasLayer;
        }

        if (!selectedFeatures.length) {
            notify(NOTIFY_TYPE.ERROR, translate('Common.AnyAreasSelected'));
            return;
        }

        const group = d3.select(`#${selectedLayer.id}`);

        selectedFeatures.forEach(f => {
            const id = f.properties.Id;
            if (!id) return;

            this._manualPaints.set(id, color);
            f.properties._fillColor = color;

            if (!group.empty()) {
                group.selectAll('path')
                    .each(function () {
                        const data = this.__data__;
                        if (!data || !data.properties) return;

                        const props = data.properties;
                        if (props.Id === id) {
                            d3.select(this)
                                .transition()
                                .duration(300)
                                .attr('fill', color);
                        }
                    });
            }
        });

        selectedFeatures.forEach(f => {
            const id = f.properties.Id;
            if (!id) return;

            $(`#SelectedAreasAfterSales-selection-${id}`).iCheck('uncheck');
            $(`#MarkedAreaAfterSales-selection-${id}`).iCheck('uncheck');
        });
    }

    _applyManualColors(layer = this.getLayer()) {
        const group = d3.select(`#${layer.id}`);
        if (group.empty()) return;

        group.selectAll('path')
            .each(function () {
                const data = this.__data__;
                if (!data || !data.properties) return;

                const id = data.properties.Id;
                if (!id) return;

                const fill = this._manualPaints.get(id);
                if (fill) {
                    d3.select(this)
                        .transition()
                        .duration(300)
                        .attr('fill', fill);
                }
            }.bind(this));
    }

}

export default ShapesAfterSalesControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/AfterSales/ShapesAfterSalesControl.js